// The `api_token` wire shape — the roster row `GET /v1/tokens` serves, the same
// row the MCP `token list` action serves through `apps/api/src/deps/
// token-control.ts`, and what `apps/dashboard/src/lib/tokens.ts` mirrors.
//
// The SECRET is not on this shape and never can be: the plaintext credential
// exists for exactly one response (the mint) and the hash never leaves the
// store, so a projection that could carry either would be a bug the type system
// had no way to state. Everything here is either a label or a date.
//
// `expires_iso` is the field this resource is most worth having for. It answers
// "is this credential still good?" on every read, not only in the one-time mint
// modal — `null` means permanent BY POLICY (`TokenMintReason`), never "we did
// not look". A door that dropped it would let an expired `oauth_grant` PAT read
// as active forever.

import { z } from 'zod';
import { defineResource } from './resource';

/**
 * The camelCase shape the projection reads — structural, so the resource states
 * its own minimum. `ApiTokenSummary` (`apps/api/src/routes/tokens.ts`) satisfies
 * it, and that type is already the masked row: no hash field exists on it.
 */
export type ApiTokenEntity = {
  id: string;
  name: string | null;
  prefix: string;
  scopes: string[];
  lastUsedAt: Date | null;
  createdAt: Date;
  revokedAt: Date | null;
  expiresAt: Date | null;
  runnerId: string | null;
  /** Whether THIS caller may rename and revoke the row (#7426) — a per-caller fact. */
  canManage: boolean;
};

/** The published body. Field names and nullability are the existing contract. */
export const ApiTokenWireSchema = z.object({
  id: z.string(),
  /**
   * The human label, and the only field on this row that tells two credentials
   * apart — `prefix` is the same literal on every row and the secret is shown
   * once. Null = never named.
   */
  name: z.string().nullable(),
  prefix: z.string(),
  scopes: z.array(z.string()),
  last_used_iso: z.string().nullable(),
  created_iso: z.string(),
  revoked_iso: z.string().nullable(),
  /** When the row dies on its own. Null = permanent by policy, not an omission. */
  expires_iso: z.string().nullable(),
  /** A box connection token (#824); null on a human-minted PAT. */
  runner_id: z.string().nullable(),
  /**
   * Whether the CALLER that read this roster may rename and revoke this row
   * (#7426) — the server's own fence verdict (`tokenReach`, `@developerz/domain`),
   * so a client offers exactly the controls the doors honour. An owner manages
   * every row; a member or admin is shown only its own rows, all manageable; a
   * viewer's own rows read `false`.
   */
  can_manage: z.boolean(),
});

/** What a client should type against for a token roster row. */
export type ApiTokenWire = z.infer<typeof ApiTokenWireSchema>;

export const ApiTokenResource = defineResource<ApiTokenEntity, typeof ApiTokenWireSchema>({
  schema: ApiTokenWireSchema,
  serialize: (t) => ({
    id: t.id,
    name: t.name,
    prefix: t.prefix,
    scopes: t.scopes,
    last_used_iso: t.lastUsedAt ? t.lastUsedAt.toISOString() : null,
    created_iso: t.createdAt.toISOString(),
    revoked_iso: t.revokedAt ? t.revokedAt.toISOString() : null,
    expires_iso: t.expiresAt ? t.expiresAt.toISOString() : null,
    runner_id: t.runnerId,
    can_manage: t.canManage,
  }),
});
